/**
 * AuditTrailViewer - Beautiful, modular UI for displaying audit trail JSON
 * Supports the full audit trail structure from export_audit_trail endpoint
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  FileText,
  Database,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  X,
  Layers,
  Settings,
  BarChart3,
  MapPin,
  FileCheck,
  Code,
  FileType,
} from 'lucide-react';
import { exportAuditTrailToMarkdown, exportAuditTrailToDOCX } from '../../utils/auditTrailExport';

interface AuditTrailData {
  export_metadata?: {
    export_date?: string;
    project_id?: string;
    project_name?: string;
    entity_types?: string[];
    total_entities?: number;
    total_artifacts?: number;
    total_jobs?: number;
  };
  artifact_to_entity_mapping?: Array<{
    artifact_id: string;
    filename: string;
    artifact_type: string;
    file_size_bytes?: number;
    entity_ids: string[];
  }>;
  artifacts?: Array<{
    artifact_id: string;
    filename: string;
    artifact_type: string;
    file_size_bytes?: number;
    num_chunks?: number;
    num_entities?: number;
    chunks?: Array<{
      chunk_id: string;
      chunk_strategy: string;
      position: number;
      content_length: number;
    }>;
  }>;
  job_statistics?: {
    total_jobs?: number;
    total_chunks_created?: number;
    total_entities_created?: number;
    total_processing_time_seconds?: number;
    jobs?: Array<{
      job_id: string;
      job_type?: string;
      job_status?: string;
      chunking_strategy?: string;
      index_types?: string[];
      processing_time_seconds?: number;
    }>;
  };
  data_pipeline_methodology?: {
    chunking_strategy?: {
      primary_strategy?: string;
      strategies_used?: string[];
      index_classifications?: string[];
      classification_summary?: Record<string, boolean>;
    };
    entity_extraction_methodology?: {
      approach?: string;
      validation?: string;
      policy_pack_config?: any;
    };
    processing_approach?: {
      description?: string;
      average_processing_time_seconds?: number;
    };
    quality_measures?: {
      validation_protocols?: any[];
      data_quality_checks?: any[];
      warnings?: string[];
    };
  };
  policy_pack_details?: {
    id?: string;
    name?: string;
    version?: string;
    description?: string;
    rules?: any;
  };
  entity_statistics?: {
    by_type?: Record<string, number>;
    with_artifact_trace?: number;
    without_artifact_trace?: number;
  };
  sources?: Array<{
    source_id: string;
    name?: string;
    source_type?: string;
    num_artifacts?: number;
  }>;
  pfas_specific_details?: any;
}

interface AuditTrailViewerProps {
  data: AuditTrailData;
  onClose?: () => void;
  onExport?: () => void;
}

export function AuditTrailViewer({ data, onClose, onExport }: AuditTrailViewerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['export_metadata', 'artifact_to_entity_mapping', 'entity_statistics'])
  );
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(2)}h`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const SectionHeader = ({
    id,
    icon: Icon,
    title,
    badge,
    badgeColor = 'blue',
  }: {
    id: string;
    icon: React.ElementType;
    title: string;
    badge?: string | number;
    badgeColor?: 'blue' | 'green' | 'purple' | 'orange' | 'red';
  }) => {
    const isExpanded = expandedSections.has(id);
    const badgeColors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800',
    };

    return (
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-theme-elevated hover:bg-theme-surface-hover rounded-lg transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-theme-text-secondary" />
          ) : (
            <ChevronRight className="w-5 h-5 text-theme-text-secondary" />
          )}
          <Icon className="w-5 h-5 text-theme-text-secondary" />
          <h3 className="text-lg font-semibold text-theme-text">{title}</h3>
        </div>
        {badge !== undefined && (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeColors[badgeColor]}`}>
            {badge}
          </span>
        )}
      </button>
    );
  };

  const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
    <div className={`bg-theme-paper rounded-lg border border-theme-border p-4 ${className}`}>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-theme-paper rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-border">
          <div className="flex items-center gap-3">
            <FileCheck className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-theme-text">Audit Trail Report</h2>
              {data.export_metadata?.project_name && (
                <p className="text-sm text-theme-text-secondary">{data.export_metadata.project_name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-theme-paper rounded-lg shadow-lg border border-theme-border z-50">
                  <div className="py-1">
                    {onExport && (
                      <button
                        onClick={() => {
                          onExport();
                          setShowExportMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-theme-surface-hover flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        Export JSON
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const typesLabel = Array.isArray(data.export_metadata?.entity_types)
                          ? data.export_metadata.entity_types.length === 1
                            ? data.export_metadata.entity_types[0]
                            : `${data.export_metadata.entity_types.length}_types`
                          : 'all_types';
                        const filename = `audit_trail_${typesLabel}_${new Date().toISOString().split('T')[0]}.md`;
                        exportAuditTrailToMarkdown(data, filename);
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-theme-surface-hover flex items-center gap-2"
                    >
                      <FileType className="w-4 h-4" />
                      Export Markdown
                    </button>
                    <button
                      onClick={async () => {
                        const typesLabel = Array.isArray(data.export_metadata?.entity_types)
                          ? data.export_metadata.entity_types.length === 1
                            ? data.export_metadata.entity_types[0]
                            : `${data.export_metadata.entity_types.length}_types`
                          : 'all_types';
                        const filename = `audit_trail_${typesLabel}_${new Date().toISOString().split('T')[0]}.docx`;
                        try {
                          await exportAuditTrailToDOCX(data, filename);
                          setShowExportMenu(false);
                        } catch (error) {
                          console.error('DOCX export failed:', error);
                          alert('DOCX export failed. Please try Markdown or JSON export instead.');
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-theme-text hover:bg-theme-surface-hover flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Export DOCX
                    </button>
                  </div>
                </div>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-theme-surface-hover rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-theme-text-secondary" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Export Metadata */}
          {data.export_metadata && (
            <div>
              <SectionHeader
                id="export_metadata"
                icon={Database}
                title="Export Metadata"
                badge={data.export_metadata.total_entities}
                badgeColor="blue"
              />
              {expandedSections.has('export_metadata') && (
                <Card className="mt-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-theme-text-secondary">Export Date</div>
                      <div className="text-sm font-medium text-theme-text">
                        {formatDate(data.export_metadata.export_date)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-theme-text-secondary">Total Entities</div>
                      <div className="text-sm font-medium text-theme-text">
                        {data.export_metadata.total_entities?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-theme-text-secondary">Total Artifacts</div>
                      <div className="text-sm font-medium text-theme-text">
                        {data.export_metadata.total_artifacts?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-theme-text-secondary">Total Jobs</div>
                      <div className="text-sm font-medium text-theme-text">
                        {data.export_metadata.total_jobs?.toLocaleString()}
                      </div>
                    </div>
                    {data.export_metadata.entity_types && (
                      <div className="col-span-2 md:col-span-3">
                        <div className="text-xs text-theme-text-secondary mb-1">Entity Types</div>
                        <div className="flex flex-wrap gap-2">
                          {data.export_metadata.entity_types.map((type) => (
                            <span
                              key={type}
                              className="px-2 py-1 text-xs bg-theme-elevated text-theme-text rounded"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Entity Statistics */}
          {data.entity_statistics && (
            <div>
              <SectionHeader
                id="entity_statistics"
                icon={BarChart3}
                title="Entity Statistics"
                badge={data.entity_statistics.with_artifact_trace}
                badgeColor="green"
              />
              {expandedSections.has('entity_statistics') && (
                <Card className="mt-2">
                  <div className="space-y-3">
                    {data.entity_statistics.by_type && (
                      <div>
                        <div className="text-sm font-medium text-theme-text mb-2">
                          Entities by Type
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(data.entity_statistics.by_type).map(([type, count]) => (
                            <div
                              key={type}
                              className="flex items-center justify-between p-2 bg-theme-elevated rounded"
                            >
                              <span className="text-sm text-theme-text-secondary">{type}</span>
                              <span className="text-sm font-semibold text-theme-text">
                                {count.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-theme-border">
                      <div>
                        <div className="text-xs text-theme-text-secondary">With Artifact Trace</div>
                        <div className="text-lg font-semibold text-green-600">
                          {data.entity_statistics.with_artifact_trace?.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-theme-text-secondary">Without Artifact Trace</div>
                        <div className="text-lg font-semibold text-red-600">
                          {data.entity_statistics.without_artifact_trace?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Artifact to Entity Mapping */}
          {data.artifact_to_entity_mapping && data.artifact_to_entity_mapping.length > 0 && (
            <div>
              <SectionHeader
                id="artifact_to_entity_mapping"
                icon={Package}
                title="Artifact to Entity Mapping"
                badge={data.artifact_to_entity_mapping.length}
                badgeColor="purple"
              />
              {expandedSections.has('artifact_to_entity_mapping') && (
                <Card className="mt-2">
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {data.artifact_to_entity_mapping.map((mapping) => (
                      <div
                        key={mapping.artifact_id}
                        className="p-3 bg-theme-elevated rounded border border-theme-border"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-theme-text">{mapping.filename}</div>
                            <div className="text-xs text-theme-text-secondary mt-1">
                              {mapping.artifact_type} • {formatBytes(mapping.file_size_bytes)}
                            </div>
                          </div>
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                            {mapping.entity_ids.length} entities
                          </span>
                        </div>
                        <div className="text-xs font-mono text-theme-text-secondary break-all">
                          {mapping.artifact_id}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Job Statistics */}
          {data.job_statistics && (
            <div>
              <SectionHeader
                id="job_statistics"
                icon={Clock}
                title="Job Statistics"
                badge={data.job_statistics.total_jobs}
                badgeColor="orange"
              />
              {expandedSections.has('job_statistics') && (
                <Card className="mt-2">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-theme-text-secondary">Total Jobs</div>
                        <div className="text-lg font-semibold text-theme-text">
                          {data.job_statistics.total_jobs?.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-theme-text-secondary">Chunks Created</div>
                        <div className="text-lg font-semibold text-theme-text">
                          {data.job_statistics.total_chunks_created?.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-theme-text-secondary">Entities Created</div>
                        <div className="text-lg font-semibold text-theme-text">
                          {data.job_statistics.total_entities_created?.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-theme-text-secondary">Total Processing Time</div>
                        <div className="text-lg font-semibold text-theme-text">
                          {formatDuration(data.job_statistics.total_processing_time_seconds)}
                        </div>
                      </div>
                    </div>
                    {data.job_statistics.jobs && data.job_statistics.jobs.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-theme-text mb-2">Jobs</div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {data.job_statistics.jobs.map((job) => (
                            <div
                              key={job.job_id}
                              className="p-3 bg-theme-elevated rounded border border-theme-border"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-sm font-medium text-theme-text">
                                  {job.job_type || 'ingest'}
                                </div>
                                <div className="flex items-center gap-2">
                                  {job.job_status === 'completed' ? (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                                  )}
                                  <span className="text-xs text-theme-text-secondary">
                                    {formatDuration(job.processing_time_seconds)}
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-theme-text-secondary font-mono">
                                {job.job_id}
                              </div>
                              {job.chunking_strategy && (
                                <div className="text-xs text-theme-text-secondary mt-1">
                                  Strategy: {job.chunking_strategy}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Data Pipeline Methodology */}
          {data.data_pipeline_methodology && (
            <div>
              <SectionHeader
                id="data_pipeline_methodology"
                icon={Settings}
                title="Data Pipeline Methodology"
                badgeColor="blue"
              />
              {expandedSections.has('data_pipeline_methodology') && (
                <Card className="mt-2">
                  <div className="space-y-4">
                    {data.data_pipeline_methodology.chunking_strategy && (
                      <div>
                        <div className="text-sm font-medium text-theme-text mb-2">
                          Chunking Strategy
                        </div>
                        <div className="p-3 bg-theme-elevated rounded">
                          <div className="text-sm text-theme-text">
                            Primary: <span className="font-semibold">
                              {data.data_pipeline_methodology.chunking_strategy.primary_strategy}
                            </span>
                          </div>
                          {data.data_pipeline_methodology.chunking_strategy.classification_summary && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {Object.entries(
                                data.data_pipeline_methodology.chunking_strategy.classification_summary
                              ).map(([key, value]) =>
                                value ? (
                                  <span
                                    key={key}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                                  >
                                    {key.replace(/_/g, ' ')}
                                  </span>
                                ) : null
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {data.data_pipeline_methodology.entity_extraction_methodology && (
                      <div>
                        <div className="text-sm font-medium text-theme-text mb-2">
                          Entity Extraction
                        </div>
                        <div className="p-3 bg-theme-elevated rounded">
                          <div className="text-sm text-theme-text">
                            {data.data_pipeline_methodology.entity_extraction_methodology.approach}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Policy Pack Details */}
          {data.policy_pack_details && (
            <div>
              <SectionHeader
                id="policy_pack_details"
                icon={FileCheck}
                title="Policy Pack Details"
                badge={data.policy_pack_details.version}
                badgeColor="green"
              />
              {expandedSections.has('policy_pack_details') && (
                <Card className="mt-2">
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-theme-text-secondary">Name</div>
                      <div className="text-sm font-medium text-theme-text">
                        {data.policy_pack_details.name}
                      </div>
                    </div>
                    {data.policy_pack_details.description && (
                      <div>
                        <div className="text-xs text-theme-text-secondary">Description</div>
                        <div className="text-sm text-theme-text">
                          {data.policy_pack_details.description}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Sources */}
          {data.sources && data.sources.length > 0 && (
            <div>
              <SectionHeader
                id="sources"
                icon={MapPin}
                title="Data Sources"
                badge={data.sources.length}
                badgeColor="blue"
              />
              {expandedSections.has('sources') && (
                <Card className="mt-2">
                  <div className="space-y-2">
                    {data.sources.map((source) => (
                      <div
                        key={source.source_id}
                        className="p-3 bg-theme-elevated rounded border border-theme-border"
                      >
                        <div className="font-medium text-theme-text">{source.name || 'Unnamed Source'}</div>
                        <div className="text-xs text-theme-text-secondary mt-1">
                          {source.source_type} • {source.num_artifacts} artifacts
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Artifacts Detail */}
          {data.artifacts && data.artifacts.length > 0 && (
            <div>
              <SectionHeader
                id="artifacts"
                icon={FileText}
                title="Artifacts Detail"
                badge={data.artifacts.length}
                badgeColor="purple"
              />
              {expandedSections.has('artifacts') && (
                <Card className="mt-2">
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {data.artifacts.map((artifact) => (
                      <div
                        key={artifact.artifact_id}
                        className="p-3 bg-theme-elevated rounded border border-theme-border"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-theme-text">{artifact.filename}</div>
                            <div className="text-xs text-theme-text-secondary mt-1">
                              {artifact.artifact_type} • {formatBytes(artifact.file_size_bytes)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {artifact.num_chunks !== undefined && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                {artifact.num_chunks} chunks
                              </span>
                            )}
                            {artifact.num_entities !== undefined && (
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                {artifact.num_entities} entities
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

